// ============================================================================
// adminKezdolapView.js — "Kezdőlap" az admin panelben (2026-10-25, a titkár #86 / #92 javaslata): az admin kijelöli, mely szobák gombjai
// jelennek meg a tagok kezdőlapján ("Hasznos tudnivalók"), és milyen sorrendben. CSAK admin (a szerver is csak adminnak engedi).
// A gombok felirata a szoba saját, a tag nyelvén megjelenő neve; a tag csak azt a szobát látja a kezdőlapon, amelyhez hozzáfér.
// ============================================================================

import { t } from "./i18n.js";
import { el } from "./dom.js";
import { getRoomDisplayName } from "./roomName.js";
import { szobaFaSorrend } from "./roomTree.js";
import { temaCim } from "./forumData.js";

export const KEZDOLAP_MAX_SZOBA = 8;

/**
 * @param {object} model { uiLang, contentLang, rooms (listRooms), homeRoomIds: null (töltődik) | string[], busy, homeStatus: null | "saved" | "failed" }
 * @param {object} handlers onHomeAdd(roomId), onHomeRemove(roomId), onHomeMove(roomId, irany: -1|1), onHomeSave
 */
export function renderKezdolapBeallitas(model, handlers) {
  const { uiLang, contentLang, rooms, homeRoomIds, busy } = model;
  const szekcio = el("section", { class: "panel-section admin-home" });
  szekcio.appendChild(el("h3", { text: t(uiLang, "admin.homeMenu") }));
  szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.homeHint", { n: KEZDOLAP_MAX_SZOBA }) }));
  if (!Array.isArray(homeRoomIds)) {
    szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
    return szekcio;
  }
  const elo = (rooms || []).filter((r) => r && !r.deleting && !r.archived);
  const nev = (r) => getRoomDisplayName(r, contentLang, uiLang);

  if (homeRoomIds.length === 0) szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.homeEmpty") }));
  const lista = el("div", { class: "admin-home-list" });
  homeRoomIds.forEach((id, i) => {
    const r = elo.find((x) => x.roomId === id);
    const sor = el("div", { class: "admin-home-row" });
    sor.appendChild(el("span", { class: "admin-home-name", text: r ? nev(r) : id }));
    sor.appendChild(el("button", { type: "button", class: "neutral-btn compact", text: "▲", disabled: busy || i === 0, "aria-label": "▲", onclick: () => handlers.onHomeMove(id, -1) }));
    sor.appendChild(el("button", { type: "button", class: "neutral-btn compact", text: "▼", disabled: busy || i === homeRoomIds.length - 1, "aria-label": "▼", onclick: () => handlers.onHomeMove(id, 1) }));
    sor.appendChild(el("button", { type: "button", class: "danger-btn compact", text: "✕", disabled: busy, "aria-label": t(uiLang, "admin.homeRemove"), onclick: () => handlers.onHomeRemove(id) }));
    lista.appendChild(sor);
  });
  szekcio.appendChild(lista);

  if (homeRoomIds.length < KEZDOLAP_MAX_SZOBA) {
    const kinalat = szobaFaSorrend(elo).filter(({ room }) => !homeRoomIds.includes(room.roomId));
    const valaszto = el("select", { class: "panel-input admin-home-add", "aria-label": t(uiLang, "admin.homeAdd") });
    valaszto.appendChild(el("option", { value: "", text: t(uiLang, "admin.homeAdd") }));
    for (const { room, melyseg } of kinalat) {
      valaszto.appendChild(el("option", { value: room.roomId, text: "  ".repeat(melyseg) + (melyseg > 0 ? "↳ " : "") + nev(room) }));
    }
    valaszto.addEventListener("change", () => { if (valaszto.value) handlers.onHomeAdd(valaszto.value); });
    szekcio.appendChild(valaszto);
  }

  // --- Belépési cél (v153, a titkár #94 kérése): a kezdőlap helyett egy meglévő szoba / téma nyíljon 1 óra szünet után
  const cel = model.homeLanding || null;
  szekcio.appendChild(el("h4", { class: "admin-home-landing-title", text: t(uiLang, "admin.homeLanding") }));
  szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.homeLandingHint") }));
  if (model.homeLandingIssueAt) szekcio.appendChild(el("div", { class: "panel-status err admin-home-issue", text: t(uiLang, "admin.homeLandingIssue") }));
  for (const [kulcs, felirat] of [["home", "admin.homeLandingHome"], ["topic", "admin.homeLandingTopic"]]) {
    const cimke = el("label", { class: "panel-check" });
    const r = el("input", { type: "radio", name: "home-landing", value: kulcs });
    r.checked = (kulcs === "topic") === Boolean(cel);
    r.addEventListener("change", () => handlers.onHomeLandingMode(kulcs));
    cimke.appendChild(r);
    cimke.appendChild(el("span", { text: " " + t(uiLang, felirat) }));
    szekcio.appendChild(cimke);
  }
  if (cel) {
    const szoba = el("select", { class: "panel-input admin-home-landing-room", "aria-label": t(uiLang, "admin.homeLandingRoom") });
    szoba.appendChild(el("option", { value: "", text: t(uiLang, "admin.homeLandingRoom") }));
    for (const { room, melyseg } of szobaFaSorrend(elo)) {
      const o = el("option", { value: room.roomId, text: "  ".repeat(melyseg) + (melyseg > 0 ? "↳ " : "") + nev(room) });
      if (room.roomId === cel.roomId) o.selected = true;
      szoba.appendChild(o);
    }
    szoba.addEventListener("change", () => handlers.onHomeLandingRoom(szoba.value));
    szekcio.appendChild(szoba);
    if (cel.roomId) {
      const tema = el("select", { class: "panel-input admin-home-landing-topic", "aria-label": t(uiLang, "admin.homeLandingTopic") });
      tema.appendChild(el("option", { value: "", text: t(uiLang, "admin.homeLandingNoTopic") }));
      const temak = Array.isArray(model.homeLandingTopics) ? model.homeLandingTopics.filter((x) => x.kind === "discussion") : [];
      for (const x of temak) {
        const o = el("option", { value: x.topicId, text: temaCim(x, contentLang) });
        if (x.topicId === cel.topicId) o.selected = true;
        tema.appendChild(o);
      }
      if (cel.topicId && !temak.some((x) => x.topicId === cel.topicId)) {
        const o = el("option", { value: cel.topicId, text: cel.topicId });
        o.selected = true;
        tema.appendChild(o);
      }
      tema.addEventListener("change", () => handlers.onHomeLandingTopic(tema.value));
      szekcio.appendChild(tema);
      // --- Most olvasásra ajánlott tájékoztatók (v157, a titkár #96 javaslata): legfeljebb 3 meglévő hozzászólás a témából
      if (cel.topicId) {
        szekcio.appendChild(el("h4", { class: "admin-home-pinned-title", text: t(uiLang, "admin.homePinned") }));
        szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.homePinnedHint") }));
        const valasztott = new Set(model.homePinned || []);
        const uzenetek = Array.isArray(model.homeLandingMessages) ? model.homeLandingMessages : null;
        if (model.homeLandingMessagesHiba === true) {
          szekcio.appendChild(el("div", { class: "panel-status err admin-home-pinned-hiba", text: t(uiLang, "admin.homePinnedFailed") }));
          szekcio.appendChild(el("button", { type: "button", class: "neutral-btn compact admin-home-pinned-ujra", text: t(uiLang, "search.refresh"), disabled: busy, onclick: () => handlers.onHomePinnedReload && handlers.onHomePinnedReload() }));
        } else if (uzenetek === null) szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
        else if (uzenetek.length === 0) szekcio.appendChild(el("p", { class: "hint", text: t(uiLang, "admin.homePinnedNone") }));
        else {
          const lista = el("div", { class: "panel-list admin-home-pinned" });
          for (const u of uzenetek) {
            const sor = el("label", { class: "panel-check" });
            const cb = el("input", { type: "checkbox" });
            cb.checked = valasztott.has(u.messageId);
            cb.disabled = !cb.checked && valasztott.size >= 3;
            cb.addEventListener("change", () => handlers.onHomePinnedToggle(u.messageId, cb.checked));
            sor.appendChild(cb);
            sor.appendChild(el("span", { text: " " + (Number.isInteger(u.postNumber) ? `#${u.postNumber} · ` : "") + u.excerpt }));
            lista.appendChild(sor);
            // Saját, rövid címke a kijelölt bejegyzéshez (v164): ez áll a hirdetmény fejlécében (üresen: a bejegyzés első sora, a tag nyelvén).
            if (cb.checked) {
              const cimke = el("input", { type: "text", class: "panel-input admin-home-pinned-label", maxlength: "60", placeholder: t(uiLang, "admin.homePinnedLabel"), "aria-label": t(uiLang, "admin.homePinnedLabel") });
              cimke.value = (model.homePinnedLabels && model.homePinnedLabels[u.messageId]) || "";
              cimke.addEventListener("input", () => handlers.onHomePinnedLabel && handlers.onHomePinnedLabel(u.messageId, cimke.value));
              lista.appendChild(cimke);
            }
          }
          szekcio.appendChild(lista);
        }
      }
    }
  }

  if (model.homeStatus === "saved") szekcio.appendChild(el("div", { class: "panel-status ok", text: t(uiLang, "admin.homeSaved") }));
  if (model.homeStatus === "failed") szekcio.appendChild(el("div", { class: "panel-status err", text: t(uiLang, "admin.homeFailed") }));
  szekcio.appendChild(el("div", { class: "panel-row" }, [
    el("button", { type: "button", class: "primary-btn compact admin-home-save", text: t(uiLang, "admin.homeSave"), disabled: busy, onclick: handlers.onHomeSave }),
  ]));
  return szekcio;
}
